/**
 * EditarCantidadPartidaModal
 * Modal para editar solo la cantidad de una partida en un presupuesto
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import { toast } from 'react-toastify';
import './EditarCantidadPartidaModal.css';

const EditarCantidadPartidaModal = ({ partida, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    cantidad: '',
    fecha_inicio: '',
    fecha_termino: ''
  });
  const [loading, setLoading] = useState(false);
  const [proyectoFechas, setProyectoFechas] = useState({
    fecha_inicio_planeado: null,
    fecha_termino_planeada: null
  });

  useEffect(() => {
    if (partida) {
      setFormData({
        cantidad: partida.cantidad || '1',
        fecha_inicio: partida.fecha_inicio || '',
        fecha_termino: partida.fecha_termino || ''
      });
      // Cargar fechas del proyecto
      loadProyectoFechas();
    }
  }, [partida]);

  const loadProyectoFechas = async () => {
    try {
      if (!partida || !partida.area) {
        console.log('❌ No hay partida o área');
        return;
      }

      console.log('🔄 Cargando fechas del proyecto para partida:', partida.nombre);
      console.log('📋 Area ID:', partida.area);

      // Cargar el área para obtener el presupuesto de costo
      const areaResponse = await presupuestoHierarchyService.getCostoAreaHierarchy(partida.area);
      const area = areaResponse.data;
      console.log('📦 Área cargada:', area);

      // El presupuesto_costo puede venir como ID o como objeto
      const presupuestoCostoId = area.presupuesto_costo_data?.id || area.presupuesto_costo;
      console.log('💰 Presupuesto de costo ID:', presupuestoCostoId);

      if (!presupuestoCostoId) {
        console.log('⚠️ No hay presupuesto de costo asociado');
        return;
      }

      // Cargar el presupuesto de costo completo
      const presupuestoResponse = await presupuestoHierarchyService.getPresupuestoCosto(presupuestoCostoId);
      const presupuestoCosto = presupuestoResponse.data;
      console.log('📊 Presupuesto de costo completo:', presupuestoCosto);

      // Obtener el ID del proyecto desde el presupuesto de costo
      const proyectoId = presupuestoCosto.proyecto_data?.id || presupuestoCosto.proyecto;
      console.log('🎯 Proyecto ID:', proyectoId);

      if (!proyectoId) {
        console.log('⚠️ No hay proyecto asociado al presupuesto');
        return;
      }

      // Cargar el proyecto completo con sus fechas
      const proyectoResponse = await presupuestoHierarchyService.getProyecto(proyectoId);
      const proyecto = proyectoResponse.data;
      console.log('📋 Proyecto completo:', proyecto);
      console.log('📅 Fecha inicio planeado:', proyecto.fecha_inicio_planeado);
      console.log('📅 Fecha término planeada:', proyecto.fecha_termino_planeada);

      setProyectoFechas({
        fecha_inicio_planeado: proyecto.fecha_inicio_planeado || null,
        fecha_termino_planeada: proyecto.fecha_termino_planeada || null
      });
      console.log('✅ Fechas del proyecto establecidas');
    } catch (error) {
      console.error('❌ Error loading proyecto fechas:', error);
      // No mostrar error al usuario, las fechas son opcionales
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.cantidad || parseFloat(formData.cantidad) <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    setLoading(true);
    try {
      // Actualizar la cantidad y las fechas de la partida de costo
      const updateData = {
        cantidad: parseFloat(formData.cantidad)
      };

      // Solo incluir las fechas si están presentes
      if (formData.fecha_inicio) {
        updateData.fecha_inicio = formData.fecha_inicio;
      }
      if (formData.fecha_termino) {
        updateData.fecha_termino = formData.fecha_termino;
      }

      await presupuestoHierarchyService.updateCostoPartida(partida.id, updateData);

      toast.success('Partida actualizada exitosamente');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating partida:', error);
      toast.error('Error al actualizar la partida');
    } finally {
      setLoading(false);
    }
  };

  if (!partida) return null;

  const modalContent = (
    <div className="modal-overlay-fullscreen">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Editar Partida - {partida.nombre}</h2>
          <button
            className="close-btn"
            onClick={onClose}
            disabled={loading}
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="cantidad">Cantidad *</label>
            <input
              type="number"
              id="cantidad"
              name="cantidad"
              value={formData.cantidad}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              disabled={loading}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="fecha_inicio">
              Fecha de Inicio
              {proyectoFechas.fecha_inicio_planeado && proyectoFechas.fecha_termino_planeada && (
                <span style={{ fontSize: '0.85em', color: '#6b7280', marginLeft: '0.5rem' }}>
                  (Rango: {proyectoFechas.fecha_inicio_planeado} - {proyectoFechas.fecha_termino_planeada})
                </span>
              )}
            </label>
            <input
              type="date"
              id="fecha_inicio"
              name="fecha_inicio"
              value={formData.fecha_inicio}
              onChange={handleChange}
              min={proyectoFechas.fecha_inicio_planeado || ''}
              max={proyectoFechas.fecha_termino_planeada || ''}
              disabled={loading}
            />
            {proyectoFechas.fecha_inicio_planeado && proyectoFechas.fecha_termino_planeada && (
              <small style={{ color: '#6b7280', fontSize: '0.85em' }}>
                📅 Debe estar entre {proyectoFechas.fecha_inicio_planeado} y {proyectoFechas.fecha_termino_planeada}
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="fecha_termino">
              Fecha de Término
              {proyectoFechas.fecha_inicio_planeado && proyectoFechas.fecha_termino_planeada && (
                <span style={{ fontSize: '0.85em', color: '#6b7280', marginLeft: '0.5rem' }}>
                  (Rango: {proyectoFechas.fecha_inicio_planeado} - {proyectoFechas.fecha_termino_planeada})
                </span>
              )}
            </label>
            <input
              type="date"
              id="fecha_termino"
              name="fecha_termino"
              value={formData.fecha_termino}
              onChange={handleChange}
              min={proyectoFechas.fecha_inicio_planeado || ''}
              max={proyectoFechas.fecha_termino_planeada || ''}
              disabled={loading}
            />
            {proyectoFechas.fecha_inicio_planeado && proyectoFechas.fecha_termino_planeada && (
              <small style={{ color: '#6b7280', fontSize: '0.85em' }}>
                📅 Debe estar entre {proyectoFechas.fecha_inicio_planeado} y {proyectoFechas.fecha_termino_planeada}
              </small>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.getElementById('modal-root'));
};

export default EditarCantidadPartidaModal;
